"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TooltipWidth = void 0;
const tslib_1 = require("tslib");
const ARIA_1 = tslib_1.__importDefault(require("../util/ARIA"));
const DismissableTooltipPosition_1 = require("./DismissableTooltipPosition");
const GlobalCounter_1 = tslib_1.__importDefault(require("../util/GlobalCounter"));
const ARIARole_1 = tslib_1.__importDefault(require("../util/ARIARole"));
const DOM_1 = tslib_1.__importDefault(require("../util/DOM"));
const REMCalculator_1 = tslib_1.__importDefault(require("../util/REMCalculator"));
const WCAG_1 = tslib_1.__importStar(require("../util/WCAG"));
const HTMLUtils_1 = tslib_1.__importDefault(require("../util/HTMLUtils"));
class ToggleTip {
    static createToggleTip(associatedElement, tooltipPromise, options) {
        const defaultOptions = {
            tooltipWidth: TooltipWidth.CUSTOM,
            buttonBackgroundColor: WCAG_1.ButtonBackgroundColor.DEFAULT,
            hoverEffect: true,
            focusEffect: true,
            reRenderOnAssociatedElementClick: false
        };
        const mergedOptions = Object.assign({}, defaultOptions, options);
        const cellElement = associatedElement instanceof HTMLTableCellElement;
        if (!cellElement) {
            associatedElement.style.position = "relative";
        }
        associatedElement.setAttribute("role", ARIARole_1.default.BUTTON);
        if (mergedOptions.focusEffect) {
            associatedElement.setAttribute(ARIA_1.default.LABEL, "toggle-tip");
        }
        associatedElement.setAttribute("tabindex", "0");
        associatedElement.setAttribute(ARIA_1.default.EXPANDED, "false");
        const tooltipContainer = document.createElement("div");
        associatedElement.classList.add("wcag-tooltip-element", mergedOptions.buttonBackgroundColor);
        tooltipContainer.classList.add("wcag-tooltip");
        tooltipContainer.setAttribute("role", ARIARole_1.default.STATUS);
        const tooltipId = GlobalCounter_1.default.next();
        tooltipContainer.id = tooltipId;
        associatedElement.setAttribute(ARIA_1.default.CONTROLS, tooltipId);
        associatedElement.appendChild(tooltipContainer);
        ToggleTip.addListeners(associatedElement, tooltipContainer, tooltipPromise, mergedOptions);
    }
    static addListeners(associatedElement, tooltipContainer, tooltipPromise, options) {
        let hovered = false;
        let focused = false;
        if (options.focusEffect && !options.associatedElementEvent) {
            associatedElement.addEventListener("click", event => {
                // Only show and hide on mobile devices, click should otherwise only just set focus state
                if (!hovered) {
                    ToggleTip.showOrHide(associatedElement, tooltipContainer, event, tooltipPromise, options);
                }
                focused = !focused;
                ToggleTip.setHoverFocusState(associatedElement, hovered, focused, options);
            });
            tooltipContainer.addEventListener("click", event => {
                // Prevent associatedElement from closing the tooltip if a user wants to copy text
                event.preventDefault();
                event.stopPropagation();
            });
        }
        if (options.associatedElementEvent && !options.focusEffect) {
            associatedElement.addEventListener(options.associatedElementEvent.eventType, event => {
                ToggleTip.showOrHide(associatedElement, tooltipContainer, event, tooltipPromise, options);
            });
        }
        associatedElement.addEventListener("mouseenter", event => {
            if (!focused) {
                ToggleTip.showOrHide(associatedElement, tooltipContainer, event, tooltipPromise, options);
            }
            hovered = true;
            ToggleTip.setHoverFocusState(associatedElement, hovered, focused, options);
        });
        associatedElement.addEventListener("mouseleave", event => {
            if (!focused) {
                ToggleTip.showOrHide(associatedElement, tooltipContainer, event, tooltipPromise, options);
            }
            hovered = false;
            ToggleTip.setHoverFocusState(associatedElement, hovered, focused, options);
        });
        associatedElement.addEventListener("keydown", event => {
            if (WCAG_1.default.SPACE_KEY === event.key) {
                event.preventDefault();
            }
        });
        associatedElement.addEventListener("keyup", event => {
            let newFocus = undefined;
            const hasCustomEventListener = options.associatedElementEvent && !options.focusEffect;
            if (event.key === "Tab") {
                newFocus = true;
            }
            else if (focused && event.key === "Escape") {
                newFocus = false;
                event.stopPropagation();
                event.preventDefault();
            }
            else if (!hasCustomEventListener && !focused && event.key === WCAG_1.default.SPACE_KEY) {
                newFocus = true;
            }
            if (newFocus !== undefined) {
                focused = newFocus;
                ToggleTip.setHoverFocusState(associatedElement, hovered, focused, options);
                if (!hovered) {
                    ToggleTip.showOrHide(associatedElement, tooltipContainer, event, tooltipPromise, options);
                }
            }
        });
        associatedElement.addEventListener("focusout", event => {
            focused = false;
            if (!hovered) {
                ToggleTip.showOrHide(associatedElement, tooltipContainer, event, tooltipPromise, options);
            }
            ToggleTip.setHoverFocusState(associatedElement, hovered, focused, options);
        });
    }
    static async showOrHide(associatedElement, tooltipContainer, event, tooltipPromise, options) {
        if (event.type === "focusout" || event.type === "mouseleave") {
            ToggleTip.setTooltipVisibility({
                associatedElement,
                tooltipContainer,
                visible: false,
                options
            });
            return;
        }
        const { eventType, reRenderOnAssociatedElementEvent } = options.associatedElementEvent || {};
        if (event.type === eventType && reRenderOnAssociatedElementEvent) {
            ToggleTip.setTooltipVisibility({
                associatedElement,
                tooltipContainer,
                visible: false,
                clearTooltip: false,
                options
            });
        }
        const toBecomeVisible = !tooltipContainer.classList.contains("visible");
        if (toBecomeVisible) {
            const result = await tooltipPromise();
            if (!result) {
                ToggleTip.setTooltipVisibility({
                    associatedElement,
                    tooltipContainer,
                    visible: false,
                    options
                });
                return;
            }
            if (options.tooltipWidth === TooltipWidth.CUSTOM) {
                const currentWidth = tooltipContainer.getBoundingClientRect().width;
                const currentWidthInRem = REMCalculator_1.default.pxToRem(currentWidth);
                tooltipContainer.style.minWidth = `${Math.min(currentWidthInRem, 12.5)}rem`;
            }
            ToggleTip.setTooltipVisibility({
                associatedElement,
                tooltipContainer,
                visible: true,
                clearTooltip: false,
                options
            });
            ToggleTip.render(tooltipContainer, result);
            ToggleTip.positionTooltip(associatedElement, tooltipContainer, DismissableTooltipPosition_1.DismissableTooltipPosition.AUTO, options.tooltipWidth, options.maxWidthRem);
        }
        else {
            ToggleTip.setTooltipVisibility({
                associatedElement,
                tooltipContainer,
                visible: false,
                options
            });
        }
    }
    static setTooltipVisibility({ associatedElement, visible, options, tooltipContainer, clearTooltip = true }) {
        associatedElement.setAttribute(ARIA_1.default.EXPANDED, `${visible}`);
        tooltipContainer.classList.toggle("visible", visible);
        const tooltipWidth = options.tooltipWidth;
        if ((!visible && tooltipWidth) || clearTooltip) {
            setTimeout(() => {
                if (!visible && tooltipWidth) {
                    tooltipContainer.classList.remove(tooltipWidth);
                }
                if (clearTooltip) {
                    ToggleTip.clearTooltip(tooltipContainer);
                }
            }, ToggleTip.TOGGLE_TIP_FADE_OUT_MS);
        }
    }
    static setHoverFocusState(associatedElement, hovered, focused, options) {
        if (options.hoverEffect) {
            associatedElement.classList.toggle("wcag-tooltip-hovered", hovered);
        }
        if (options.focusEffect) {
            associatedElement.classList.toggle("wcag-tooltip-focused", focused);
        }
    }
    static render(tooltip, toolTipElement) {
        ToggleTip.clearTooltip(tooltip);
        const { header, bodyHtml, bodyText } = toolTipElement;
        let headerElement;
        let bodyElement;
        if (bodyHtml || bodyText) {
            headerElement = document.createElement("p");
            headerElement.classList.add("h3", "bold");
            bodyElement = document.createElement("p");
            if (bodyText) {
                bodyElement.innerText = bodyText;
            }
            else if (bodyHtml) {
                bodyElement.innerHTML = HTMLUtils_1.default.sanitizeHTML(bodyHtml);
            }
        }
        else {
            headerElement = document.createElement("p");
        }
        headerElement.textContent = header;
        tooltip.appendChild(headerElement);
        if (bodyElement) {
            tooltip.appendChild(bodyElement);
        }
    }
    static clearTooltip(tooltip) {
        while (tooltip.firstChild) {
            tooltip.removeChild(tooltip.firstChild);
        }
    }
    static positionTooltip(associatedElement, tooltipContainer, position, tooltipWidth = TooltipWidth.CUSTOM, maxWidth) {
        if (position === DismissableTooltipPosition_1.DismissableTooltipPosition.AUTO) {
            for (const positionElement of Object.values(DismissableTooltipPosition_1.DismissableTooltipPosition)) {
                if (positionElement === DismissableTooltipPosition_1.DismissableTooltipPosition.AUTO) {
                    continue;
                }
                const point = ToggleTip.getTooltipPoint(associatedElement, tooltipContainer, positionElement);
                ToggleTip.transformXY(tooltipContainer, positionElement, point.x, point.y, tooltipWidth);
                // Check if it breaks max width -> set width to max width
                if (maxWidth) {
                    const pixelWidth = tooltipContainer.getBoundingClientRect().width;
                    const remWidth = REMCalculator_1.default.pxToRem(pixelWidth);
                    if (remWidth > maxWidth) {
                        ToggleTip.transformXY(tooltipContainer, positionElement, point.x, point.y, tooltipWidth, maxWidth);
                    }
                }
                if (DOM_1.default.isElementFullyVisible(tooltipContainer)) {
                    return;
                }
                tooltipContainer.classList.remove(positionElement);
            }
            // default to top if it wasn't possible to find a render position
            const { x, y } = ToggleTip.getTooltipPoint(associatedElement, tooltipContainer, position);
            ToggleTip.transformXY(tooltipContainer, DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_RIGHT, x, y, tooltipWidth);
        }
        else {
            const { x, y } = ToggleTip.getTooltipPoint(associatedElement, tooltipContainer, position);
            ToggleTip.transformXY(tooltipContainer, position, x, y, tooltipWidth);
        }
    }
    static transformXY(tooltip, position, x, y, tooltipWidth, customWidth) {
        tooltip.className = "";
        tooltip.style.top = `${y}px`;
        tooltip.style.left = `${x}px`;
        if (customWidth) {
            tooltip.classList.add("wcag-tooltip", "visible", position);
            tooltip.style.minWidth = `${customWidth}rem`;
        }
        else {
            tooltip.classList.add("wcag-tooltip", "visible", position, tooltipWidth);
        }
    }
    static getTooltipPoint(associatedElement, tooltip, position) {
        if (associatedElement instanceof HTMLTableCellElement) {
            return ToggleTip.getTHTooltipPoint(associatedElement, tooltip, position);
        }
        let x = 0;
        let y = 0;
        switch (position) {
            case DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_RIGHT:
                x = 0;
                y = associatedElement.clientHeight * 1.25;
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_LEFT:
                x = associatedElement.clientWidth - tooltip.clientWidth;
                y = associatedElement.clientHeight * 1.25;
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.TOP_RIGHT:
                x = 0;
                y = -tooltip.clientHeight - (associatedElement.clientHeight * 0.25);
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.TOP_LEFT:
                x = associatedElement.clientWidth - tooltip.clientWidth;
                y = -tooltip.clientHeight - (associatedElement.clientHeight * 0.25);
                break;
        }
        return { x, y };
    }
    static getTHTooltipPoint(associatedElement, tooltip, position) {
        const TOOLTIP_PADDING_VERTICAL = 7;
        let x = 0;
        let y = 0;
        switch (position) {
            case DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_RIGHT:
                x = 0;
                y = associatedElement.clientHeight + TOOLTIP_PADDING_VERTICAL;
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.BOTTOM_LEFT:
                x = associatedElement.clientWidth - tooltip.clientWidth;
                y = associatedElement.clientHeight + TOOLTIP_PADDING_VERTICAL;
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.TOP_RIGHT:
                x = 0;
                y = -tooltip.clientHeight;
                break;
            case DismissableTooltipPosition_1.DismissableTooltipPosition.TOP_LEFT:
                x = associatedElement.clientWidth - tooltip.clientWidth;
                y = -tooltip.clientHeight;
                break;
        }
        return { x, y };
    }
}
ToggleTip.TOGGLE_TIP_FADE_OUT_MS = 150;
exports.default = ToggleTip;
var TooltipWidth;
(function (TooltipWidth) {
    TooltipWidth["M"] = "wcag-tooltip-m";
    TooltipWidth["L"] = "wcag-tooltip-l";
    TooltipWidth["XL"] = "wcag-tooltip-xl";
    TooltipWidth["CUSTOM"] = "wcag-tooltip-custom";
})(TooltipWidth = exports.TooltipWidth || (exports.TooltipWidth = {}));
//# sourceMappingURL=ToggleTip.js.map